import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  getSession,
  login as loginApi,
  logout as logoutApi,
  type SessionUser,
} from "@/api/auth";

interface AuthContextValue {
  user: SessionUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = async () => {
    const session = await getSession();
    setUser(session.authenticated ? session.user : null);
  };

  useEffect(() => {
    refresh().finally(() => setIsLoading(false));
  }, []);

  const login = async (username: string, password: string) => {
    const session = await loginApi(username, password);
    setUser(session.user);
  };

  const logout = async () => {
    await logoutApi();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: user !== null,
        login,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
